import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import {
  Loader2,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  BarChart3,
  RefreshCw,
  Download,
  Ban
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { authFetch, handleResponse } from '@/utils/auth-fetch';

interface Sector {
  id: number;
  name: string;
  description?: string;
}

interface BatchJob {
  id: string;
  openai_batch_id?: string;
  sector_id: number;
  sector_name?: string;
  status: string;
  total_topics: number;
  completed_topics: number;
  failed_topics: number;
  metadata?: any;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  request_counts?: {
    total: number;
    completed: number;
    failed: number;
  };
}

interface PendingCount {
  count: number;
  languages: string[];
}

const statusColors: Record<string, string> = {
  creating: 'bg-blue-100 text-blue-800',
  validating: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-purple-100 text-purple-800',
  finalizing: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-orange-100 text-orange-800'
};

const statusIcons: Record<string, React.ReactNode> = {
  creating: <Loader2 className="h-4 w-4 animate-spin" />,
  validating: <Clock className="h-4 w-4" />,
  in_progress: <Loader2 className="h-4 w-4 animate-spin" />,
  finalizing: <Clock className="h-4 w-4" />,
  completed: <CheckCircle2 className="h-4 w-4" />,
  failed: <XCircle className="h-4 w-4" />,
  expired: <AlertCircle className="h-4 w-4" />,
  cancelled: <Ban className="h-4 w-4" />
};

export default function BatchResearchManagement() {
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadBatchJobs();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadBatchJobs();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadBatchJobs = async () => {
    try {
      setRefreshing(true);
      const response = await authFetch('/api/batch-research/list?limit=50');
      const data = await handleResponse<{ jobs: BatchJob[] }>(response);
      setBatchJobs(data.jobs);
    } catch (error) {
      console.error('Error loading batch jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load batch jobs',
        variant: 'destructive'
      });
    } finally {
      setRefreshing(false);
    }
  };


  const handleProcessResults = async (jobId: string) => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/batch-research/process/${jobId}`, {
        method: 'POST'
      });

      const data = await handleResponse<{
        success: boolean;
        successCount: number;
        failCount: number;
      }>(response);

      toast({
        title: 'Success',
        description: `Processed ${data.successCount} topics successfully, ${data.failCount} failed.`
      });

      loadBatchJobs();
    } catch (error: any) {
      console.error('Error processing results:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process results',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to cancel this batch job?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await authFetch(`/api/batch-research/cancel/${jobId}`, {
        method: 'POST'
      });

      await handleResponse(response);

      toast({
        title: 'Success',
        description: 'Batch job cancelled successfully'
      });

      loadBatchJobs();
    } catch (error: any) {
      console.error('Error cancelling job:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel job',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateProgress = (job: BatchJob) => {
    if (job.total_topics === 0) return 0;
    return Math.round(((job.completed_topics + job.failed_topics) / job.total_topics) * 100);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Batch Research Monitoring</h1>
          <p className="text-muted-foreground">
            Monitor automatic batch research jobs created by the topic-research-agent
          </p>
        </div>
        <Button onClick={loadBatchJobs} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Info Banner - Automatic Processing */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Automatic Batch Processing</h3>
                <p className="text-sm text-blue-800 mt-1">
                  Batch jobs are created automatically by the topic-research-agent when there are 20+ pending topics.
                  Simply approve topics in the sector management, and the agent will handle batch creation, monitoring, and result processing.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-blue-800 mt-2">
                  <li>Agent checks every 5 minutes for pending topics</li>
                  <li>Automatically creates batch jobs when threshold is reached</li>
                  <li>Monitors job progress and processes results when complete</li>
                  <li>50% cost savings compared to real-time API</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Batch Jobs List */}
      <Card>
        <CardHeader>
          <CardTitle>Batch Jobs</CardTitle>
          <CardDescription>
            Monitor and manage all batch research jobs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Topics</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No batch jobs found. Create your first batch job above.
                    </TableCell>
                  </TableRow>
                ) : (
                  batchJobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Badge className={statusColors[job.status] || 'bg-gray-100 text-gray-800'}>
                          <span className="mr-1">{statusIcons[job.status]}</span>
                          {job.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{job.sector_name || `Sector ${job.sector_id}`}</div>
                          {job.metadata?.languageFilter && (
                            <div className="text-xs text-muted-foreground">
                              Language: {job.metadata.languageFilter}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>Total: {job.total_topics}</div>
                          <div className="text-xs text-muted-foreground">
                            ✓ {job.completed_topics} | ✗ {job.failed_topics}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-blue-500 h-2 transition-all duration-300"
                                style={{ width: `${calculateProgress(job)}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground min-w-[3rem] text-right">
                              {calculateProgress(job)}%
                            </span>
                          </div>
                          {job.request_counts && (
                            <div className="text-xs text-muted-foreground">
                              {job.request_counts.completed}/{job.request_counts.total} requests
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatDate(job.created_at)}</div>
                          {job.created_by_name && (
                            <div className="text-xs text-muted-foreground">
                              by {job.created_by_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {job.status === 'completed' && job.completed_topics < job.total_topics && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleProcessResults(job.id)}
                              disabled={loading}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Process
                            </Button>
                          )}
                          {(job.status === 'validating' || job.status === 'in_progress') && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancelJob(job.id)}
                              disabled={loading}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="space-y-2 text-sm text-green-900">
            <h3 className="font-semibold">How the Automatic System Works</h3>
            <div className="space-y-3 text-green-800">
              <div>
                <strong>Real-Time Mode ({"<"}20 topics):</strong>
                <ul className="list-disc list-inside ml-2 space-y-1 mt-1">
                  <li>Topics researched immediately using standard API</li>
                  <li>Fast results (2-5 minutes per topic)</li>
                  <li>Suitable for small batches</li>
                </ul>
              </div>
              <div>
                <strong>Batch Mode (≥20 topics):</strong>
                <ul className="list-disc list-inside ml-2 space-y-1 mt-1">
                  <li>Automatically creates OpenAI Batch API jobs</li>
                  <li>50% cost savings</li>
                  <li>Process up to 50,000 topics per batch</li>
                  <li>Typical completion: 24 hours (often faster)</li>
                  <li>Results automatically saved when complete</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
